import { useRef, useState } from "react";
import { toPng } from "html-to-image";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Download, PartyPopper, Flame, Mail } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import Mascot from "@/components/Mascot";
import { cheerFor } from "@/lib/cheers";

export default function SouvenirCard({ open, onOpenChange, challenge, parentName }) {
  const ref = useRef(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const share = async (e) => {
    e.preventDefault();
    setSending(true);
    let image_b64 = null;
    try {
      if (ref.current) {
        image_b64 = await toPng(ref.current, { pixelRatio: 2, backgroundColor: "#FAF8F5" });
      }
    } catch {
      image_b64 = null;
    }
    try {
      await api.post(`/challenges/${challenge.id}/share`, { to_email: email, message, image_b64 });
      toast.success(`Carte souvenir envoyée à ${email} 💛`);
      setShareOpen(false);
      setEmail("");
      setMessage("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Envoi impossible pour le moment");
    } finally {
      setSending(false);
    }
  };

  if (!challenge) return null;

  const download = async () => {
    if (!ref.current) return;
    try {
      const dataUrl = await toPng(ref.current, { pixelRatio: 2, backgroundColor: "#FAF8F5" });
      const link = document.createElement("a");
      link.download = `parentia-souvenir-${challenge.title.replace(/\s+/g, "-").toLowerCase()}.png`;
      link.href = dataUrl;
      link.click();
      toast.success("Carte souvenir téléchargée 🎉");
    } catch (e) {
      toast.error("Impossible de générer l'image");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rounded-3xl bg-[#FAF8F5] max-h-[90vh] overflow-y-auto" data-testid="souvenir-dialog">
        <DialogTitle className="sr-only">Carte souvenir : {challenge.title}</DialogTitle>
        <div
          className="flex items-center gap-3 px-6 pt-5"
          data-testid="kyno-cheer"
        >
          <Mascot pose="trophy" size={92} anim="wiggle" className="shrink-0 mascot-pop" />
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-[#77849C]">Kyno te félicite</p>
            <p className="mt-1 text-lg font-semibold text-[#16233D]" style={{ fontFamily: "Outfit, sans-serif" }}>
              {cheerFor(challenge.id || challenge.title)}
            </p>
          </div>
        </div>
        <div ref={ref} className="p-6" data-testid="souvenir-card">
          <div
            className="rounded-3xl p-8 text-center"
            style={{
              background:
                "linear-gradient(135deg, #FBE2E9 0%, #FFF4DC 45%, #E6F5EF 100%)",
            }}
          >
            <div className="mx-auto h-14 w-14 rounded-2xl bg-white/70 grid place-items-center mb-3">
              <PartyPopper className="h-7 w-7 text-[#E8899F]" />
            </div>
            <p className="text-xs font-bold uppercase tracking-widest text-[#16233D]">
              Défi famille accompli
            </p>
            <h2
              className="mt-2 text-3xl font-bold text-[#16233D]"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              {challenge.title}
            </h2>
            <p className="mt-3 text-[#44506B]">{challenge.description}</p>
            <div className="mt-6 flex items-center justify-center gap-2 text-[#E8899F] font-bold text-2xl">
              <Flame className="h-6 w-6" /> {challenge.duration_days} jour{challenge.duration_days > 1 ? "s" : ""}
            </div>
            <div className="mt-6 flex justify-center gap-1">
              {Array.from({ length: challenge.duration_days }).map((_, i) => (
                <span
                  key={i}
                  className="h-2 w-2 rounded-full"
                  style={{ background: "#23375E" }}
                />
              ))}
            </div>
            <p className="mt-6 text-sm text-[#16233D]">
              Bravo{parentName ? `, ${parentName}` : ""} 💛
            </p>
            <p className="mt-1 text-xs text-[#77849C]">Kynoria · {new Date().toLocaleDateString("fr-FR")}</p>
          </div>
        </div>
        <div className="px-6 pb-6 space-y-3">
          <button
            data-testid="download-souvenir-btn"
            onClick={download}
            className="w-full brand-btn rounded-full py-3 flex items-center justify-center gap-2 font-medium"
          >
            <Download className="h-4 w-4" /> Télécharger la carte
          </button>

          {!shareOpen ? (
            <button
              data-testid="share-souvenir-btn"
              onClick={() => setShareOpen(true)}
              className="w-full rounded-full border border-[#E3E8F1] bg-white py-3 flex items-center justify-center gap-2 font-medium text-[#44506B] hover:border-[#23375E] transition-colors duration-200"
            >
              <Mail className="h-4 w-4" /> Envoyer à la famille
            </button>
          ) : (
            <form onSubmit={share} className="space-y-2 rounded-2xl border border-[#E3E8F1] bg-white p-4">
              <label className="text-xs font-bold uppercase tracking-wider text-[#77849C]">
                Email du destinataire
              </label>
              <input
                data-testid="share-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="mamie@example.fr"
                className="w-full rounded-xl border border-[#E3E8F1] px-3 py-2 text-sm outline-none focus:border-[#23375E] transition-colors"
              />
              <input
                data-testid="share-message-input"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Un petit mot (facultatif)"
                className="w-full rounded-xl border border-[#E3E8F1] px-3 py-2 text-sm outline-none focus:border-[#23375E] transition-colors"
              />
              <button
                data-testid="share-send-btn"
                type="submit"
                disabled={sending}
                className="w-full brand-btn rounded-full py-2.5 font-medium disabled:opacity-60"
              >
                {sending ? "Envoi en cours…" : "Envoyer la carte souvenir"}
              </button>
            </form>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
